import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, Icon, Text, Tooltip } from 'core/components';
import { Field, Form, Select } from 'core/form/components';
import { isInSubnet } from 'core/util/ip';
import { getIPSortValue } from 'core/util/sortUtils';
import { hideTooltips } from 'app/views/synthetics/utils/syntheticsUtils';
import ReachabilityTimeline from '../ReachabilityTimeline';
import PathGraph from '../PathGraph';

const fields = {
  prefix: {
    label: 'Select monitored prefix',
    placeholder: 'Select a prefix',
    options: [],
    defaultValue: 'all'
  },
  subPrefix: {
    label: 'Select a more specific prefix',
    options: [],
    defaultValue: 'all'
  }
};

@inject('$exports', '$bgp', '$dataviews')
@Form({ fields, options: { name: 'Bgp Test Results Graph Controller Options' } })
@observer
class BgpDataViz extends Component {
  constructor(props) {
    super(props);

    // Set up form change handler for prefix selection
    this.setupFormChangeHandler();
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const { startDate, endDate, selectedPrefix } = this.props;

    // Reload data when date range changes
    if (prevProps.startDate !== startDate || prevProps.endDate !== endDate) {
      this.loadData();
    }

    // Handle external prefix selection (from beta version)
    if (prevProps.selectedPrefix !== selectedPrefix && selectedPrefix) {
      this.setPrefixes(selectedPrefix);
    }
  }

  loadData = async () => {
    // Load all necessary data in parallel for better performance
    await Promise.all([this.loadSubPrefixes(), this.loadReachabilityTimeseries(), this.loadASPathGraphTimeseries()]);
  };

  setupFormChangeHandler = () => {
    const { form } = this.props;

    form.onChange = ({ field, fieldValue }) => {
      if (field?.name === 'prefix') {
        // When prefix changes, update sub-prefix selection
        this.updateSubPrefixSelection(fieldValue);
      }
    };
  };

  loadSubPrefixes = async () => {
    const { $bgp, model, startDate, endDate } = this.props;
    const { include_covered } = model.get('config.bgp');

    // Don't load sub-prefixes if include_covered is not enabled
    if (!include_covered) {
      this.initializePrefixSelections();
      return;
    }

    const configuredPrefixes = this.getConfiguredPrefixes();

    await $bgp.fetchSubPrefixes({
      prefixes: configuredPrefixes,
      includeCovered: include_covered,
      startDate,
      endDate
    });

    // After loading, update selections if needed
    this.initializePrefixSelections();
  };

  loadReachabilityTimeseries = async () => {
    const { $bgp, model, startDate, endDate } = this.props;
    const { include_covered } = model.get('config.bgp');
    const configuredPrefixes = this.getConfiguredPrefixes();

    await $bgp.fetchReachabilityTimeseries({
      prefixes: configuredPrefixes,
      includeCovered: include_covered,
      startDate,
      endDate
    });
  };

  loadASPathGraphTimeseries = async () => {
    const { $bgp, model, startDate, endDate } = this.props;
    const { include_covered } = model.get('config.bgp');
    const configuredPrefixes = this.getConfiguredPrefixes();

    await $bgp.fetchASPathGraphTimeseries({
      prefixes: configuredPrefixes,
      includeCovered: include_covered,
      startDate,
      endDate
    });
  };

  initializePrefixSelections = () => {
    const { form } = this.props;
    const configuredPrefixes = this.getConfiguredPrefixes();

    // Auto-select if there's only one prefix
    if (configuredPrefixes.length === 1 && form.getValue('prefix') === 'all') {
      form.setValue('prefix', configuredPrefixes[0]);
    }

    // Auto-select sub-prefix if needed
    const currentPrefix = form.getValue('prefix');
    if (currentPrefix && currentPrefix !== 'all') {
      this.updateSubPrefixSelection(currentPrefix);
    }
  };

  updateSubPrefixSelection = (selectedPrefix) => {
    const { form, $bgp } = this.props;

    // Reset to 'all' first
    form.setValue('subPrefix', 'all');

    if (!selectedPrefix || selectedPrefix === 'all') {
      return;
    }

    // Get filtered sub-prefixes for the selected prefix
    const subPrefixes = $bgp.subPrefixesByPrefix(selectedPrefix) || [];

    // Auto-select if there's only one sub-prefix
    if (subPrefixes.length === 1) {
      form.setValue('subPrefix', subPrefixes[0]);
    }
  };

  getConfiguredPrefixes = () => {
    const { model } = this.props;
    const { prefix } = model.get('config.bgp');
    return prefix.split(',').map((p) => p.trim());
  };

  shouldUpdate = ({ startDate, endDate }, prevProps) =>
    prevProps.startDate !== startDate || prevProps.endDate !== endDate;

  handleMouseOut = () => {
    const { $dataviews } = this.props;
    hideTooltips($dataviews);
  };

  // setPrefix is used by the AS path graph to filter to a more specific prefix.
  setPrefix = (prefix) => {
    const { form } = this.props;
    const currPrefix = form.getValue('subPrefix');
    const fieldValue = prefix === currPrefix ? null : prefix;

    form.setValue('subPrefix', fieldValue);
    form.onChange({ field: { name: 'subPrefix' }, fieldValue });
  };

  getBaseGraphProps = () => {
    const { model, startDate, endDate, loading } = this.props;
    const { shouldUpdate, setPrefix } = this;

    return {
      model,
      startDate,
      endDate,
      loading,
      shouldUpdate,
      setPrefix
    };
  };

  sortPrefixes = (prefixes, forceToTop = null) => {
    // Create a copy to avoid mutating the original array
    const sorted = [...prefixes];

    sorted.sort((a, b) => {
      // Force a specific prefix to the top if specified
      if (forceToTop && forceToTop !== 'all') {
        if (a === forceToTop) {
          return -1;
        }
        if (b === forceToTop) {
          return 1;
        }
      }

      // Extract prefix lengths
      const lengthA = parseInt(a.split('/')[1], 10);
      const lengthB = parseInt(b.split('/')[1], 10);

      // Sort by prefix length first (ascending order - shorter prefixes first)
      if (lengthA !== lengthB) {
        return lengthA - lengthB;
      }

      // If prefix lengths are equal, sort numerically by IP address
      const ipValueA = getIPSortValue(a);
      const ipValueB = getIPSortValue(b);

      // Handle cases where getIPSortValue might return the original value
      if (typeof ipValueA === 'number' && typeof ipValueB === 'number') {
        return ipValueA - ipValueB;
      }

      // Fallback to string comparison
      return a.localeCompare(b);
    });

    return sorted;
  };

  renderPrefixControls = () => {
    const { $bgp, model } = this.props;
    const { include_covered } = model.get('config.bgp');
    const configuredPrefixes = this.getConfiguredPrefixes();

    // Sort the configured prefixes
    const sortedPrefixes = this.sortPrefixes(configuredPrefixes);

    const prefixOptions = [
      ...(configuredPrefixes.length > 1 ? [{ label: 'All Prefixes', value: 'all' }] : []),
      ...sortedPrefixes.map((v) => ({ label: v, value: v }))
    ];

    const hasSubPrefixes = $bgp.hasSubPrefixes && $bgp.subPrefixesData.length > 1;
    const shouldShowSubPrefixes = include_covered && hasSubPrefixes;

    return (
      <Flex alignItems="center" gap={1}>
        <Field name="prefix" options={prefixOptions} large mb={0}>
          <Select />
        </Field>
        {shouldShowSubPrefixes && this.renderSubPrefixControl()}
      </Flex>
    );
  };

  subPrefixOptionFormatter = (props) => {
    const { label } = props;
    // Check if this sub-prefix is an exact match for a configured prefix, if it is, bold it
    return this.getConfiguredPrefixes().includes(label) ? <strong>{label}</strong> : label;
  };

  renderSubPrefixControl = () => {
    const { $bgp, form } = this.props;
    const selectedPrefix = form.getValue('prefix');

    // Get sub-prefixes filtered by selected prefix, or all if 'all' is selected
    const subPrefixes =
      (selectedPrefix && selectedPrefix !== 'all' ? $bgp.subPrefixesByPrefix(selectedPrefix) : $bgp.subPrefixesData) ||
      [];

    // Sort sub-prefixes by prefix length, then numerically, with the selected prefix forced to the top
    const sortedSubPrefixes = this.sortPrefixes(subPrefixes, selectedPrefix);

    const subPrefixOptions = sortedSubPrefixes.map((subPfx) => ({
      label: subPfx,
      value: subPfx
    }));

    // Show 'All' option if:
    // 1. There are multiple sub-prefixes, OR
    // 2. A specific prefix is selected, but it's not in the original subPrefixesData list
    const prefixNotInSubPrefixes =
      selectedPrefix &&
      selectedPrefix !== 'all' &&
      $bgp.subPrefixesData &&
      !$bgp.subPrefixesData.includes(selectedPrefix);

    const shouldShowAll = subPrefixOptions.length > 1 || prefixNotInSubPrefixes;

    const options = [...(shouldShowAll ? [{ label: 'All Covered Prefixes', value: 'all' }] : []), ...subPrefixOptions];

    return (
      <Field name="subPrefix" options={options} large mb={0}>
        <Select optionFormatter={this.subPrefixOptionFormatter} />
      </Field>
    );
  };

  exceedsMaxDateRange = () => {
    const { startDate, endDate, maxDateRange } = this.props;
    return endDate - startDate > maxDateRange;
  };

  badTestConfig = () => {
    const { model, form } = this.props;
    const { include_covered } = model.get('config').bgp;
    const prefix = form.getValue('prefix');

    return !!prefix && include_covered && parseInt(prefix.split('/')[1], 10) < 15;
  };

  setPrefixes = (ip) => {
    const { form } = this.props;
    const configuredPrefixes = this.getConfiguredPrefixes();

    const prefixFound = configuredPrefixes.includes(ip);
    const prefix = prefixFound ? ip : configuredPrefixes.find((p) => isInSubnet(ip, p));

    if (prefix) {
      const subPrefix = !prefixFound ? ip : null;

      form.setValue('prefix', prefix);
      form.onChange({ field: { name: 'prefix' }, fieldValue: prefix });

      if (subPrefix) {
        form.setValue('subPrefix', subPrefix);
      }
    }
  };

  getPrefixesToRender = () => {
    const { model, form, $bgp } = this.props;
    const { include_covered } = model.get('config.bgp');
    const configuredPrefixes = this.getConfiguredPrefixes();

    const selectedPrefix = form.getValue('prefix');
    const selectedSubPrefix = form.getValue('subPrefix');

    // If a specific sub-prefix is selected, render only that
    if (selectedSubPrefix && selectedSubPrefix !== 'all') {
      return [selectedSubPrefix];
    }

    // If a specific prefix is selected (but subPrefix is 'all'), render all its sub-prefixes
    if (selectedPrefix && selectedPrefix !== 'all') {
      const subPrefixes = $bgp.subPrefixesByPrefix(selectedPrefix) || [];
      if (subPrefixes.length > 0 && include_covered) {
        return subPrefixes;
      }
      // If no sub-prefixes available, return the selected prefix itself
      return [selectedPrefix];
    }

    // If 'all' is selected or no selection, render all monitored prefixes
    // Include covered prefixes based on test config
    if (include_covered) {
      const allSubPrefixes = $bgp.subPrefixesData || [];
      return allSubPrefixes.length > 0 ? allSubPrefixes : configuredPrefixes;
    }
    // Otherwise, return the configured prefixes
    return configuredPrefixes;
  };

  render() {
    const {
      xAxisMin,
      xAxisMax,
      setSubsetTimeRange,
      subsetStartDate,
      subsetEndDate,
      onTimelineHover,
      selectedTimeMs,
      hoveredDimension
    } = this.props;

    if (this.exceedsMaxDateRange()) {
      return (
        <Flex flex="1 0 auto" flexDirection="column" justifyContent="center" p={2}>
          <Text as="div" textAlign="center">
            Please select a date range of 30 days or less.
          </Text>
        </Flex>
      );
    }

    const overrideStartEndDate =
      subsetStartDate && subsetEndDate ? { startDate: subsetStartDate, endDate: subsetEndDate } : {};
    const prefixes = this.getPrefixesToRender();

    return (
      <Flex flex="1 0 auto" flexDirection="column">
        <Flex mb={1} alignItems="center" gap={3}>
          {this.renderPrefixControls()}

          {this.badTestConfig() && (
            <Tooltip
              content={
                <div style={{ width: 150 }}>
                  Will only include more specifics for prefixes narrower (larger) than /14
                </div>
              }
              minimal
            >
              <Icon icon="info-sign" color="primary" />
            </Tooltip>
          )}
        </Flex>
        <Flex flexDirection="column" onMouseOut={this.handleMouseOut}>
          <ReachabilityTimeline
            {...this.getBaseGraphProps()}
            xAxisMin={xAxisMin}
            xAxisMax={xAxisMax}
            onClickTimeline={setSubsetTimeRange}
            onHover={onTimelineHover}
            prefixes={prefixes}
          />
        </Flex>
        <Flex flex="1 0 auto" flexDirection="column">
          <PathGraph
            {...this.getBaseGraphProps()}
            {...overrideStartEndDate}
            prefixes={prefixes}
            selectedTimeMs={selectedTimeMs}
            hoveredAsn={hoveredDimension}
          />
        </Flex>
      </Flex>
    );
  }
}

export default BgpDataViz;
